import type { Reference, Task } from '@medplum/fhirtypes'
import { InboxItemType } from '@seen/core/models/inbox'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import type { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { DetailPanelShell } from '#src/pages/inbox/detail/DetailPanelShell.tsx'
import { ResourceUnavailable } from '#src/pages/inbox/detail/ResourceUnavailable.tsx'
import { SidebarHeader } from '#src/pages/inbox/detail/SidebarHeader.tsx'
import { TaskFocusContent } from '#src/pages/inbox/detail/TaskFocusContent.tsx'
import {
  snippetText,
  taskFocusTitle,
  taskSidebarHeaderProps,
} from '#src/pages/inbox/detail/taskTitles.ts'
import { dueLabel } from '#src/pages/inbox/TaskDueInboxItemRow.tsx'

interface UseTaskSidebarTitleResult {
  title: string
  subtitle?: string
}

/* Derive action-oriented title + subtitle for task-based notifications */
function useTaskSidebarTitle(
  notificationItem: NotificationItem,
  task: StandardTask | undefined
): UseTaskSidebarTitleResult {
  const data = notificationItem.data
  const assignerRef =
    data.type === InboxItemType.TaskAssigned ? data.assigner : undefined
  const commentAuthorRef =
    data.type === InboxItemType.TaskNewComment &&
    isOfType(data.comment.authorReference, 'Practitioner')
      ? data.comment.authorReference
      : undefined

  const { data: assigner } = useRead(ExtendedPractitioner, assignerRef)
  const { data: commenter } = useRead(ExtendedPractitioner, commentAuthorRef)

  const subtitle = task ? taskFocusTitle(task) : undefined

  if (data.type === InboxItemType.TaskAssigned) {
    const title = assigner
      ? `${assigner.name} assigned this to you`
      : 'Assigned to you'
    return { title, subtitle }
  }

  if (data.type === InboxItemType.TaskDue && task) {
    const dueText = dueLabel(task)
    if (task.description) {
      return {
        title: task.description,
        subtitle: subtitle ? `${subtitle} \u00b7 ${dueText}` : dueText,
      }
    }
    return { title: dueText, subtitle }
  }

  if (data.type === InboxItemType.TaskNewComment) {
    const snippet = snippetText(data.comment.text)
    let title: string
    if (commenter && snippet) {
      title = `${commenter.name}: ${snippet}`
    } else if (commenter) {
      title = `${commenter.name} commented`
    } else {
      title = snippet ?? 'New comment'
    }
    return { title, subtitle }
  }

  return { title: subtitle ?? 'Task' }
}

/* Task viewer -- renders the task's focus resource (order, care plan, etc.) */
export function TaskDetailView({
  taskRef,
  notificationItem,
  onClose,
}: {
  taskRef: Reference<Task>
  notificationItem: NotificationItem
  onClose: () => void
}): ReactNode {
  const { data: task, isError } = useRead(StandardTask, taskRef)
  const { title, subtitle } = useTaskSidebarTitle(notificationItem, task)

  if (isError) {
    return (
      <DetailPanelShell onClose={onClose}>
        <ResourceUnavailable label="task" />
      </DetailPanelShell>
    )
  }

  if (!task) {
    return (
      <DetailPanelShell onClose={onClose}>
        <Loader />
      </DetailPanelShell>
    )
  }

  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <SidebarHeader
        title={title}
        subtitle={subtitle}
        {...taskSidebarHeaderProps(task)}
        notificationItem={notificationItem}
        onClose={onClose}
      />
      <ScrollArea className="flex-1" scrollRestorationId="inbox-detail-task">
        <div className="p-4">
          <ErrorBoundary fallback={<ResourceUnavailable label="resource" />}>
            <TaskFocusContent task={task} />
          </ErrorBoundary>
        </div>
      </ScrollArea>
    </div>
  )
}
